import React, { useState, useMemo } from "react";
import Select from "react-select";
import trashIcon from '../../assets/images/trash-icon.png';
import { formatUSD } from '../../utils/common';

const EditCustomerSalesTable = ({
    salesData,
    setFormData,
    products,
    productMap,
    multiplierMap,
    multiplierOptions,
    isCustomer,
    rowErrors
}) => {
    const TAX_RATE = 0.00; // 0%
    const [isUpdateSales, setIsUpdateSales] = useState(false);

    // Get the first multiplier value from the options list as a default
    //const defaultMultiplier = useMemo(() => 
    //     (multiplierOptions && multiplierOptions.length > 0) ? multiplierOptions[0].value : "",
    // [multiplierOptions]);
    const defaultMultiplier = null;

    // Initializing the empty "infinite" row
    if(salesData.length > 0 && !isUpdateSales) {
        salesData[salesData.length] = {
            type: "Sale",
            product_number: "",
            description: "",
            unit_price: "0.00",
            quantity: "",
            total_price: "0.00",
            multiplier: isCustomer ? "" : defaultMultiplier 
        };
        setIsUpdateSales(true);
    }

    const parseNumber = (value) => {
        if (value === null || value === undefined || value === "") return 0;
        const num = Number(String(value).replace(/[^0-9.-]+/g, ""));
        return isNaN(num) ? 0 : num;
    };

    const recalcTotals = (rows) => {
        const sub = rows.reduce(
            (sum, r) => sum + parseNumber(r.total_price),
            0
        );
        const tax = sub * TAX_RATE;
        return {
            sub_total: sub.toFixed(2),
            sales_tax: tax.toFixed(2),
            total: (sub + tax).toFixed(2)
        };
    };

    const handleProductSelect = (index, selected) => {
        if (!selected) return;

        const product = productMap[selected.value];
        if (!product) return;

        const unitPrice = parseNumber(product.price);
        
        // Use product-specific multiplier for customers, 
        // or the existing row's multiplier (falling back to default) for staff.
        const mCode = isCustomer 
            ? product.multiplier_code 
            : (salesData[index].multiplier || defaultMultiplier);

        // console.log(multiplierMap, mCode);
        // console.log(multiplierMap[mCode]);
        
        const mNumeric = parseNumber(multiplierMap[mCode] || 1);

        setFormData(prev => {
            const rows = [...prev.sales_data];
            const qty = parseNumber(rows[index].quantity || 0);
            const totalPrice = (qty * unitPrice * mNumeric).toFixed(2);

            rows[index] = {
                ...rows[index],
                product_number: product.num,
                description: product.description,
                unit_price: unitPrice.toFixed(2),
                multiplier: parseNumber(multiplierMap[mCode] || null), 
                total_price: totalPrice
            };

            console.log("Updated row after product select:", rows[index]);

            return {
                ...prev,
                sales_data: rows,
                ...recalcTotals(rows)
            };
        });
    };

    const handleQtyChange = (index, value) => {
        const qtyNum = parseNumber(value);

        setFormData(prev => {
            const rows = [...prev.sales_data];
            const currentRow = rows[index];
            const price = parseNumber(currentRow.unit_price);
            //const mCode = currentRow.multiplier || defaultMultiplier;
            const mNumeric = parseNumber(currentRow.multiplier || 1);

            rows[index] = {
                ...currentRow,
                quantity: value,
                total_price: (qtyNum * price * mNumeric).toFixed(2)
            };

            // Infinite row logic: add a new row if we're typing in the last row
            if (qtyNum > 0 && index === rows.length - 1) {
                rows.push({
                    type: "Sale",
                    product_number: "",
                    description: "",
                    unit_price: "0.00",
                    quantity: "",
                    total_price: "0.00",
                    multiplier: mNumeric
                });
            }

            return {
                ...prev,
                sales_data: rows,
                ...recalcTotals(rows)
            };
        });
    };

    const handleMultiplierSelect = (index, selected) => {
        if (!selected) return;

        const mCode = selected.value;
        const mNumeric = parseNumber(multiplierMap[mCode] || 1);

        setFormData(prev => {
            const rows = [...prev.sales_data];
            const unitPrice = parseNumber(rows[index].unit_price || 0);
            const qty = parseNumber(rows[index].quantity || 0);

            rows[index] = {
                ...rows[index],
                multiplier: mNumeric,
                total_price: (unitPrice * mNumeric * qty).toFixed(2)
            };

            return {
                ...prev,
                sales_data: rows,
                ...recalcTotals(rows)
            };
        });
    };

    const removeRow = (index) => {
        setFormData(prev => {
            const rows = prev.sales_data.filter((_, i) => i !== index);
            return {
                ...prev,
                sales_data: rows,
                ...recalcTotals(rows)
            };
        });
    };

    return (
        <table className="table table-bordered techtop-quote-table2">
            <thead>
                <tr>
                    <th className="text-center" style={{ width: "6%" }}>Item #</th>
                    <th className="text-center">Type</th>
                    <th className="text-center" style={{ width: "25%" }}>Number</th>
                    <th className="text-center" style={{ width: (isCustomer) ? "30%" : "25%" }}>Description</th>
                    <th className="text-center">Unit Price</th>
                    <th className="text-center" style={{ width: "8%" }}>Qty</th>
                    {!isCustomer && <th className="text-center">Multiplier</th>}
                    <th className="text-center">Total</th>
                    <th className="text-center">Action</th>
                </tr>
            </thead>
            <tbody>
                {salesData.map((row, index) => (
                    <tr key={index}>
                        <td className="text-center">{index + 1}</td>
                        <td className="text-center">{row.type}</td>
                        <td>
                            <Select
                                options={products}
                                value={row.product_number ? { value: row.product_number, label: row.product_number } : null}
                                onChange={(opt) => handleProductSelect(index, opt)}
                                menuPortalTarget={document.body}
                                menuPosition="fixed"
                                styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                            />
                            {rowErrors?.[index]?.product_number && (
                                <div className="text-danger small">{rowErrors[index].product_number}</div>
                            )}
                        </td>
                        <td>{row.description}</td>
                        <td className="text-center">
                            ${formatUSD(parseNumber(row.unit_price))}
                        </td>
                        <td>
                            <input
                                type="number"
                                className="form-control quantity-input"
                                value={row.quantity}
                                onChange={(e) => handleQtyChange(index, e.target.value)}
                            />
                            {rowErrors?.[index]?.quantity && (
                                <div className="invalid-feedback d-block">{rowErrors[index].quantity}</div>
                            )}
                        </td>
                        {!isCustomer && (
                            <td>
                                <Select
                                    options={multiplierOptions}
                                    // Ensure the dropdown reflects the value stored in the row
                                    value={multiplierOptions.find(opt => opt.value === row.multiplier) || null}
                                    onChange={(opt) => handleMultiplierSelect(index, opt)}
                                    menuPortalTarget={document.body}
                                    menuPosition="fixed"
                                    styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                                />
                                {rowErrors?.[index]?.multiplier && (
                                    <div className="invalid-feedback d-block">{rowErrors[index].multiplier}</div>
                                )}
                            </td>
                        )}
                        <td className="text-center">
                            ${formatUSD(parseNumber(row.total_price))}
                        </td>
                        <td className="text-center btn-link-style">
                            {salesData.length > 1 && (
                                <img
                                    className="remove-icon"
                                    src={trashIcon}
                                    alt="Remove"
                                    style={{ cursor: 'pointer' }}
                                    onClick={() => removeRow(index)}
                                />
                            )}
                        </td>
                    </tr>
                ))}
            </tbody>
        </table>
    );
};

export default React.memo(EditCustomerSalesTable);